import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  CommanderChannelsFilterLogicalObject,
  CommanderMonitoringFilterLogicalObject,
} from '../../../pages/AuthenticatedPages/Manager/ManagerPerformances/util/filter.model';
import { MonitoringService } from '../../../service/monitoring/MonitoringService';
import { CommanderDigitalEvent } from '../../../service/monitoring/monitoring.model';
import { ZipDeviceControlUnit } from '../../controlUnit/controlUnit.model';
import { getCUsTranslationsSelector } from '../../CUTranslation/selectors';
import { FeaturesActions } from '../../features/slice/FeaturesSlice';
import { ShowLevelOption } from '../../groups/selectors/getLevelOfView';
import { AppDispatch, RootState } from '../../store.model';
import { nameReducer } from '../monitoring.model';
import { FilterStoredProps, getPatchFilter } from '../selectors';
import { TEMPERATURE_UNIT_TYPES } from '../../../service/temperaturePreference';
import _ from 'lodash';

export interface FetchMetricsArgs {
  devicesControlUnits: ZipDeviceControlUnit[];
  digitalFilterObjectEvents: CommanderMonitoringFilterLogicalObject;
  analogFilterObjectEvents: CommanderMonitoringFilterLogicalObject;
  commanderFilterObjectEvents?: CommanderChannelsFilterLogicalObject;
  start: number;
  end: number;
  showLevelOption: ShowLevelOption;
  temperaturePreference?: TEMPERATURE_UNIT_TYPES;
}

export const fetchMetrics = createAsyncThunk(
  `${nameReducer}/fetchSensorData`,
  async (
    {
      devicesControlUnits,
      start,
      end,
      digitalFilterObjectEvents,
      analogFilterObjectEvents,
      commanderFilterObjectEvents = {},
      showLevelOption,
      temperaturePreference,
    }: FetchMetricsArgs,
    thunkAPI
  ) => {
    const model = new MonitoringService();
    const CUsTranslate = getCUsTranslationsSelector(
      thunkAPI.getState() as RootState
    );

    const controlUnitState = (thunkAPI.getState() as RootState).controlUnit;

    // Fetch the commander (device level) events first so the broker connection
    // information can be used to blank out the digital channels while the device
    // was disconnected.
    // When the selected timeframe is shorter than 24h, request the commander
    // events for the last 24h so there is always enough broker connection
    // context to determine the disconnected windows.
    const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
    const commanderStart =
      end - start < ONE_DAY_IN_MS ? end - ONE_DAY_IN_MS : start;
    const dicCommanderDataList = await Promise.all(
      _.uniq(
        devicesControlUnits.map(
          (deviceControlUnit) => deviceControlUnit.commanderId
        )
      ).map((commanderId) =>
        model.getCommanderData(
          commanderId,
          commanderStart,
          end,
          commanderFilterObjectEvents[commanderId] ?? []
        )
      )
    );
    const dicCommanderData: Record<string, CommanderDigitalEvent[]> =
      Object.assign({}, ...dicCommanderDataList);

    const [dicSensordata, dicDigitalChannel] = await Promise.all([
      Promise.all(
        devicesControlUnits.map((deviceControlUnit) =>
          analogFilterObjectEvents[deviceControlUnit.deviceId]
            ? model.getSensordata(
                deviceControlUnit.deviceId,
                deviceControlUnit.commanderId,
                start,
                end,
                deviceControlUnit.deviceName ?? '',
                analogFilterObjectEvents[deviceControlUnit.deviceId] ?? {},
                CUsTranslate,
                showLevelOption,
                controlUnitState,
                temperaturePreference
              )
            : Promise.resolve({})
        )
      ),
      Promise.all(
        devicesControlUnits.map((deviceControlUnit) =>
          digitalFilterObjectEvents[deviceControlUnit.deviceId]
            ? model.getDigitalChannels(
                deviceControlUnit.deviceId,
                deviceControlUnit.commanderId,
                moment(start).add(-1, 'day').valueOf(),
                moment(end).add(1, 'day').valueOf(),
                deviceControlUnit.deviceName ?? '',
                digitalFilterObjectEvents[deviceControlUnit.deviceId] ?? {},
                CUsTranslate,
                showLevelOption,
                controlUnitState,
                dicCommanderData[deviceControlUnit.commanderId] ?? []
              )
            : Promise.resolve({})
        )
      ),
    ]);

    return [dicSensordata, dicDigitalChannel, dicCommanderDataList] as const;
  }
);

export const updateFilter =
  (
    props: FilterStoredProps & { value: CommanderMonitoringFilterLogicalObject }
  ) =>
  (dispatch: AppDispatch) => {
    const path = getPatchFilter(props);
    const stringValue = JSON.stringify(props.value);
    dispatch(FeaturesActions.updateFilter({ path, filter: stringValue }));
  };
